import { useState, useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  MousePointer, 
  Calendar, 
  Globe,
  Smartphone,
  Monitor,
  MapPin,
  Clock,
  RefreshCw
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'

interface DashboardStats {
  totalScans: number
  todayScans: number
  todayTrend: string
  totalLinks: number
  avgScansPerDay: number
  growthRate: string
  timezone?: string
  currentTime?: string
}

interface TimeOfDayStat {
  time: string
  count: number
  percentage: number
}

interface DeviceStat {
  device: string
  count: number
  percentage: number
}

interface CountryStat {
  country: string
  count: number
  percentage: number
}

interface RecentScan {
  id: string
  slug: string
  title: string
  scanned_at: string
  country: string
  device: string
}

interface TopLink {
  id: string
  slug: string
  title: string
  scans: number
  todayScans: number
  trend: string
}

interface WeeklyData {
  date: string
  day: string
  scans: number
}

export default function Stats() {
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsData, setStatsData] = useState<DashboardStats>({
    totalScans: 0,
    todayScans: 0,
    todayTrend: '+0%',
    totalLinks: 0,
    avgScansPerDay: 0,
    growthRate: '+0%'
  });
  const [deviceStats, setDeviceStats] = useState<DeviceStat[]>([]);
  const [countryStats, setCountryStats] = useState<CountryStat[]>([]);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [topLinks, setTopLinks] = useState<TopLink[]>([]);
  const [weeklyData, setWeeklyData] = useState<WeeklyData[]>([]);
  const [timeOfDayStats, setTimeOfDayStats] = useState<TimeOfDayStat[]>([]);
  
  const { user, session } = useAuth();
  const { canAccessStatistics, loading: planLoading } = usePlanLimits();
  const navigate = useNavigate();

  // Load statistics from backend
  const loadStats = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('📊 Loading statistics for range:', timeRange);

      // Load comprehensive statistics
      const statsResponse = await fetch(`${API_BASE}/api/stats/overview?range=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!statsResponse.ok) {
        throw new Error(`Backend-Fehler: ${statsResponse.status}`);
      }

      const data = await statsResponse.json();
      console.log('📊 Stats data received:', data);

      // Set all the data
      setStatsData(data.stats);
      setWeeklyData(data.weeklyData);
      setDeviceStats(data.deviceStats);
      setCountryStats(data.countryStats);
      setTopLinks(data.topLinks);
      
      // Format time of day stats if available
      if (data.timeOfDayStats) {
        const formattedTimeOfDayStats = Object.entries(data.timeOfDayStats).map(([time, count]) => {
          const totalCount = Object.values(data.timeOfDayStats).reduce((sum: number, val: any) => sum + val, 0) as number;
          return {
            time,
            count: count as number,
            percentage: totalCount > 0 ? Math.round((count as number / totalCount) * 100) : 0
          };
        });
        setTimeOfDayStats(formattedTimeOfDayStats);
      }
      
      // Format recent scans with relative time
      const formattedRecentScans = data.recentScans.map((scan: RecentScan) => {
        const scanDate = new Date(scan.scanned_at);
        const now = new Date();
        const diffMs = now.getTime() - scanDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        let timeAgo = '';
        if (diffMins < 1) {
          timeAgo = 'gerade eben';
        } else if (diffMins < 60) {
          timeAgo = `${diffMins} Min`;
        } else if (diffHours < 24) {
          timeAgo = `${diffHours} Std`;
        } else {
          timeAgo = `${diffDays} Tage`;
        }
        
        return {
          ...scan,
          time: timeAgo,
          location: scan.country || 'Unbekannt',
          device: scan.device || 'Unbekannt'
        };
      });
      
      setRecentScans(formattedRecentScans);

      console.log('✅ Statistics loaded successfully');
    } catch (err) {
      console.error('❌ Failed to load statistics:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  // Check plan access - only after loading is complete
  useEffect(() => {
    if (!planLoading && !canAccessStatistics()) {
              console.log('🔍 Stats: Redirecting to pricing - no access after loading complete');
        navigate('/qr/pricing');
      return;
    }
  }, [planLoading, canAccessStatistics, navigate]);

  useEffect(() => {
    loadStats();
  }, [session, timeRange]);

  // Show loading while plan data is being fetched
  if (planLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Lade Berechtigungen...</span>
      </div>
    );
  }

  // Don't render if no access (after loading)
  if (!planLoading && !canAccessStatistics()) {
    return null;
  }

  const StatCard = ({ title, value, change, icon: Icon, color }: any) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        {change && (
          <div className={`text-sm font-medium ${
            change.startsWith('+') ? 'text-green-600' : 
            change.startsWith('-') ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {change}
          </div>
        )}
      </div>
    </div>
  );

  const ProgressBar = ({ label, value, maxValue, percentage, color }: any) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm text-gray-500">{value} ({percentage}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${color}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );

  if (!user) {
    return <div>Nicht angemeldet</div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistiken</h1>
          <p className="text-gray-600">
            Detaillierte Analyse Ihrer QR-Code Performance
          </p>
          {statsData.timezone && (
            <p className="text-sm text-gray-500 mt-1">
              <Globe className="w-3 h-3 inline mr-1" />
              Zeitzone: {statsData.timezone.replace('_', ' ')}
              {statsData.currentTime && (
                <span className="ml-2">
                  ({new Date(statsData.currentTime).toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    timeZone: statsData.timezone 
                  })} Uhr)
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={loadStats}
            disabled={loading}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          {['24h', '7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {range === '24h' ? '24 Std' : 
               range === '7d' ? '7 Tage' : 
               range === '30d' ? '30 Tage' : 
               '90 Tage'}
            </button>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
          ❌ {error}
          <button 
            onClick={loadStats}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Statistiken werden geladen...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Gesamte Scans"
              value={statsData.totalScans.toLocaleString()}
              change={statsData.growthRate}
              icon={MousePointer}
              color="bg-blue-500"
            />
            <StatCard
              title="Heute"
              value={statsData.todayScans}
              change={statsData.todayTrend}
              icon={TrendingUp}
              color="bg-green-500"
            />
            <StatCard
              title="Durchschnitt/Tag"
              value={statsData.avgScansPerDay}
              icon={Calendar}
              color="bg-purple-500"
            />
            <StatCard
              title="Aktive Links"
              value={statsData.totalLinks}
              icon={Globe}
              color="bg-orange-500"
            />
          </div>

          {/* Main Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Weekly Chart */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Scans der letzten 7 Tage</h2>
              </div>
              <div className="p-6">
                {weeklyData.length > 0 && weeklyData.some(d => d.scans > 0) ? (
                  <div>
                    <div className="flex items-end space-x-2 h-48">
                      {weeklyData.map((day, index) => {
                        const maxScans = Math.max(...weeklyData.map(d => d.scans), 1);
                        const heightPercent = maxScans > 0 ? (day.scans / maxScans) * 100 : 0;
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center">
                            <div className="relative w-full flex flex-col items-center">
                              <span className="text-xs font-semibold text-gray-700 mb-1">
                                {day.scans}
                              </span>
                              <div 
                                className="bg-blue-500 rounded-t w-full transition-all duration-300 hover:bg-blue-600"
                                style={{ 
                                  height: `${Math.max(heightPercent * 1.5, day.scans > 0 ? 20 : 10)}px`
                                }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex space-x-2 mt-2">
                      {weeklyData.map((day, index) => (
                        <div key={index} className="flex-1 text-center">
                          <div className="text-xs font-medium text-gray-600">{day.day}</div>
                          <div className="text-xs text-gray-400">
                            {new Date(day.date).toLocaleDateString('de-DE', { 
                              day: '2-digit',
                              month: '2-digit'
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>Noch keine Scan-Daten für diesen Zeitraum verfügbar</p>
                  </div>
                )}
              </div>
            </div>

            {/* Device Stats */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Smartphone className="h-5 w-5 mr-2" />
                  Geräte-Verteilung
                </h2>
              </div>
              <div className="p-6">
                {deviceStats.length > 0 ? (
                  <div className="space-y-4">
                    {deviceStats.map((device, index) => (
                      <ProgressBar
                        key={index}
                        label={device.device}
                        value={device.count}
                        percentage={device.percentage}
                        color={index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-purple-500'}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Smartphone className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Keine Gerätedaten verfügbar</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Time of Day Statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Time of Day Distribution */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Tageszeit-Verteilung (Lokale Zeit)
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Wann werden Ihre QR-Codes gescannt (Ortszeit des Scanners)
                </p>
              </div>
              <div className="p-6">
                {timeOfDayStats.length > 0 && timeOfDayStats.some(stat => stat.count > 0) ? (
                  <div className="space-y-4">
                    {timeOfDayStats.map((stat, index) => {
                      const timeDescriptions: Record<string, string> = {
                        'Nacht': '23:00 - 04:00 Uhr',
                        'Morgen': '04:00 - 12:00 Uhr',
                        'Nachmittag': '12:00 - 18:00 Uhr',
                        'Abend': '18:00 - 23:00 Uhr'
                      };
                      const colors = ['bg-indigo-500', 'bg-yellow-500', 'bg-orange-500', 'bg-purple-500'];
                      
                      return (
                        <div key={index}>
                          <div className="flex justify-between items-center mb-1">
                            <div>
                              <span className="text-sm font-medium text-gray-700">{stat.time}</span>
                              <span className="text-xs text-gray-500 ml-2">({timeDescriptions[stat.time]})</span>
                            </div>
                            <span className="text-sm text-gray-500">{stat.count} ({stat.percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${colors[index % colors.length]}`}
                              style={{ width: `${stat.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Keine Tageszeit-Daten verfügbar</p>
                    <p className="text-xs mt-1">Neue Scans werden automatisch erfasst</p>
                  </div>
                )}
              </div>
            </div>

            {/* Best Times to Share */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Beste Zeiten</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Empfohlene Zeiten für maximale Reichweite
                </p>
              </div>
              <div className="p-6">
                {timeOfDayStats.length > 0 && timeOfDayStats.some(stat => stat.count > 0) ? (
                  <div className="space-y-4">
                    {timeOfDayStats
                      .filter(stat => stat.count > 0)
                      .sort((a, b) => b.percentage - a.percentage)
                      .slice(0, 3)
                      .map((stat, index) => {
                        const icons = ['🥇', '🥈', '🥉'];
                        const timeRecommendations: Record<string, string> = {
                          'Nacht': 'Ideal für internationale Zielgruppen',
                          'Morgen': 'Perfekt für Pendler und Frühaufsteher',
                          'Nachmittag': 'Optimale Zeit für Geschäftskunden',
                          'Abend': 'Beste Zeit für private Nutzer'
                        };
                        
                        return (
                          <div key={index} className="flex items-start space-x-3">
                            <span className="text-2xl">{icons[index]}</span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{stat.time}</div>
                              <div className="text-sm text-gray-500">{timeRecommendations[stat.time]}</div>
                              <div className="text-xs text-gray-400 mt-1">{stat.percentage}% Ihrer Scans</div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <TrendingUp className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Noch keine Daten für Empfehlungen</p>
                    <p className="text-xs mt-1">Sammeln Sie mehr Scans für personalisierte Tipps</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Geographic and Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Country Stats */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Länder-Verteilung
                </h2>
              </div>
              <div className="p-6">
                {countryStats.length > 0 ? (
                  <div className="space-y-4">
                    {countryStats.map((country, index) => {
                      const countryNames: Record<string, string> = {
                        'DE': 'Deutschland',
                        'AT': 'Österreich',
                        'CH': 'Schweiz',
                        'US': 'USA',
                        'GB': 'Großbritannien',
                        'FR': 'Frankreich',
                        'IT': 'Italien',
                        'ES': 'Spanien',
                        'NL': 'Niederlande',
                        'BE': 'Belgien',
                        'Unknown': 'Unbekannt'
                      };
                      const displayName = countryNames[country.country] || country.country;
                      
                      return (
                        <ProgressBar
                          key={index}
                          label={displayName}
                          value={country.count}
                          percentage={country.percentage}
                          color={index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-purple-500' : 'bg-gray-500'}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Keine Standortdaten verfügbar</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Scans */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Letzte Scans
                </h2>
              </div>
              <div className="p-6">
                {recentScans.length > 0 ? (
                  <div className="space-y-4 max-h-64 overflow-y-auto">
                    {recentScans.map((scan: any) => (
                      <div key={scan.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">/{scan.slug}</div>
                          <div className="text-xs text-gray-500">
                            {scan.location} • {scan.device}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          vor {scan.time}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Noch keine Scans in diesem Zeitraum</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Performing Links */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Top Performance Links</h2>
            </div>
            <div className="overflow-x-auto">
              {topLinks.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scans Gesamt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Heute
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trend
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topLinks.map((link, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">/{link.slug}</div>
                            <div className="text-xs text-gray-500">{link.title}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {link.scans}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {link.todayScans}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${
                            link.trend.startsWith('+') ? 'text-green-600' : 
                            link.trend.startsWith('-') ? 'text-red-600' : 
                            'text-gray-600'
                          }`}>
                            {link.trend}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Noch keine QR-Code Scans in diesem Zeitraum</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
} 